import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ContractingAuthorityService } from '../contracting-authority.service';
import { Router } from '@angular/router';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, SearchableSelectOption, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getContractingAuthorityFormObject } from '../utils/contracting-authority-form-object';
import { contractingAuthoritySchema } from '@/validations/contracting-authority.validation';
import { AcDto, RefParams, RefTypeEnum } from '@/types';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { forkJoin, BehaviorSubject } from 'rxjs';

@Component({
  selector: 'sm-add-contracting-authority',
  imports: [PageHeaderComponent, FormBuilderComponent, ButtonModule, TranslateModule],
  templateUrl: './add-contracting-authority.component.html',
  styleUrl: './add-contracting-authority.component.scss',
})
export class AddContractingAuthorityComponent implements OnInit {
  refParamsService = inject(RefParamsService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  contractingAuthorityRepository = inject(ContractingAuthorityRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  breadcrumb = [
    {
      label: 'settings.contractingAuthority.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.new',
      routerLink: ['/settings', 'contracting-authority', 'add'],
    },
  ];

  formObject: DynamicForm = getFormBuilderObjectFactory();

  tuttelOptions: SearchableSelectOption[] = [];
  comOptions: SelectOption[] = [];
  paysOptions: SelectOption[] = [];
  commissionTypeOptions: { name: string; code: string }[] = [
    { name: 'TUTELLE', code: 'TUTELLE' },
    { name: 'INTERNE', code: 'INTERNE' },
    { name: 'AUTRE', code: 'AUTRE' },
  ];
  commissionOptions$ = new BehaviorSubject<{ name: string; code: number }[]>([]);

  ngOnInit() {
    this.contractingAuthorityRepository.reset();

    forkJoin({
      tutels: this.contractingAuthorityService.getTutels(),
      pays: this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY),
    }).subscribe(({ tutels, pays }) => {
      this.tuttelOptions = tutels.map(ref => ({ name: ref.nom, code: ref.id, searchable: `${ref.nom}_${ref.abreviation}_${ref.nomAr}` }));
      this.paysOptions = pays.map((p: RefParams) => ({ name: p.description, code: p.id }));
      this.formObject = getContractingAuthorityFormObject({
        store: this.contractingAuthorityRepository,
        tuttelOptions: this.tuttelOptions,
        comOptions: this.comOptions,
        paysOptions: this.paysOptions,
        commissionTypeOptions: this.commissionTypeOptions,
        getCommissionOptions: () => this.commissionOptions$.value,
        onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
        isAddMode: true,
        isDetailsMode: false,
        contractingAuthorityService: this.contractingAuthorityService,
      });
    });
  }

  loadCommissionOptions(commissionTypeId: string) {
    const apiCall = this.contractingAuthorityService.getAllCommissionsForAdd();
    switch (commissionTypeId) {
      case 'TUTELLE': // Commission Tutelle
        // For add mode, use the general commissions endpoint for all types
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            const options = commissions.map((com: any) => ({ name: com.nom, code: com.id }));
            this.commissionOptions$.next(options);
            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      case 'INTERNE': // Commission Interne
        // For add mode, use the general commissions endpoint for all types
        // const apiCallInterne = this.contractingAuthorityService.getAllCommissionsForAdd();
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            const options = commissions.map((com: any) => ({ name: com.nom, code: com.id }));
            this.commissionOptions$.next(options);
            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      case 'AUTRE': // Autre Commission
        // For add mode, use the general commissions endpoint
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            const options = commissions.map((com: any) => ({ name: com.nom, code: com.id }));
            this.commissionOptions$.next(options);
            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      default:
        this.commissionOptions$.next([]);
        this.updateFormObject(); // Update form to get new commission options
    }
  }

  updateFormObject() {
    this.formObject = getContractingAuthorityFormObject({
      store: this.contractingAuthorityRepository,
      tuttelOptions: this.tuttelOptions,
      comOptions: this.comOptions,
      paysOptions: this.paysOptions,
      commissionTypeOptions: this.commissionTypeOptions,
      getCommissionOptions: () => this.commissionOptions$.value,
      onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
      isAddMode: true,
      isDetailsMode: false,
    });
  }

  addContractingAuthority() {
    const result = contractingAuthoritySchema.safeParse(this.contractingAuthorityRepository.get('dto'));
    if (result.success) {
      const dto = this.contractingAuthorityRepository.get('dto') as AcDto;
      const formData = new FormData();

      (Object.entries(dto) as [keyof AcDto, AcDto[keyof AcDto]][]).forEach(([key, value]) => {
        if (key !== 'adresse' && value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key as string, value);
          } else if (Array.isArray(value)) {
            value.forEach((item: any, index: number) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key as string, String(value));
          }
        }
      });

      // Add adresse as a single parameter
      if (dto.adresse) {
        formData.append('adresse', JSON.stringify(dto.adresse));
      }

      this.contractingAuthorityService.addContractingAuthority(formData).subscribe({
        next: response => {
          this.contractingAuthorityRepository.set('response', response);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.contractingAuthority.ac.message.success.added'),
          });
          this.router.navigate(['/settings', 'contracting-authority', 'update', this.contractingAuthorityRepository.getNested('response', 'id')]);
        },
        error: response => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: response?.error?.message || this.translate.instant('settings.contractingAuthority.ac.message.error.added'),
          });
        },
      });
    } else {
      this.contractingAuthorityRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  reset() {
    this.contractingAuthorityRepository.reset();
  }
}
